import { DEMO_IDS, DEMO_IMEI, DEMO_MQTT_USER } from './mqttPacket'

export const DEMO_SANDBOX_PARAM_DEFINITIONS = [
    {
        identificacao: 'energia',
        descricao: 'Energia',
        formula: '[energia]',
        tipo_grafico: 'linha',
        unidade: 'kWh',
        color: '#f2c94c',
    },
    {
        identificacao: 'temperatura',
        descricao: 'Temperatura',
        formula: '[temperatura]',
        tipo_grafico: 'linha',
        unidade: '°C',
        color: '#eb5757',
    },
    {
        identificacao: 'vazao',
        descricao: 'Vazão do poço',
        formula: '[vazao]',
        tipo_grafico: 'linha',
        unidade: 'm³/h',
        color: '#2f80ed',
    },
    {
        identificacao: 'nivel',
        descricao: 'Nível',
        formula: '[nivel]',
        tipo_grafico: 'linha',
        unidade: 'm',
        color: '#27ae60',
    },
    {
        identificacao: 'horas',
        descricao: 'Horas de funcionamento',
        formula: '[horas]',
        tipo_grafico: 'barra',
        unidade: 'h',
        color: '#9b51e0',
    },
    {
        identificacao: 'volume_hidrometro',
        descricao: 'Volume do hidrômetro',
        formula: '[volume_hidrometro]',
        tipo_grafico: 'barra',
        unidade: 'm³',
        color: '#56ccf2',
    },
]

const baseReadingForField = (fieldName, hourOffset) => {
    const wave = Math.sin(hourOffset / 3.2)
    const waveSecondary = Math.cos(hourOffset / 5.1)

    switch (fieldName) {
        case 'energia':
            return Number((42 + wave * 6 + (hourOffset % 4) * 0.8).toFixed(2))
        case 'temperatura':
            return Number((24.5 + wave * 3.2 + waveSecondary * 0.6).toFixed(2))
        case 'vazao':
            return Number((8.5 + wave * 2.4 + (hourOffset % 3) * 0.35).toFixed(2))
        case 'nivel':
            return Number((3.4 + waveSecondary * 0.55 + wave * 0.25).toFixed(2))
        case 'horas':
            return Number((1.2 + Math.abs(wave) * 0.8 + (hourOffset % 5) * 0.15).toFixed(2))
        case 'volume_hidrometro':
            return Number((12 + Math.abs(waveSecondary) * 4 + (hourOffset % 6) * 0.9).toFixed(2))
        case 'bateria':
            return Number((88 - (hourOffset % 10) * 0.4).toFixed(1))
        default:
            return Number((20 + wave * 4).toFixed(2))
    }
}

export const getLatestSandboxValues = () => {
    const nowHourIndex = 0
    return {
        energia: baseReadingForField('energia', nowHourIndex),
        temperatura: baseReadingForField('temperatura', nowHourIndex),
        vazao: baseReadingForField('vazao', nowHourIndex),
        nivel: baseReadingForField('nivel', nowHourIndex),
        horas: baseReadingForField('horas', nowHourIndex),
        volume_hidrometro: baseReadingForField('volume_hidrometro', nowHourIndex),
        bateria: baseReadingForField('bateria', nowHourIndex),
        timestamp_dispositivo: Math.floor(Date.now() / 1000),
    }
}

const CHART_SAMPLE_INTERVAL_IN_MINUTES = 5
const CHART_SAMPLES_IN_LAST_DAY = (24 * 60) / CHART_SAMPLE_INTERVAL_IN_MINUTES

export const buildChartSeries = (fieldName = 'temperatura', campoMeta = {}) => {
    const nowTimestamp = Math.floor(Date.now() / 1000)
    const dataPoints = []
    const values = []

    for (let sampleIndex = CHART_SAMPLES_IN_LAST_DAY - 1; sampleIndex >= 0; sampleIndex -= 1) {
        const hourOffset = (sampleIndex * CHART_SAMPLE_INTERVAL_IN_MINUTES) / 60
        const sampleValue = baseReadingForField(fieldName, hourOffset)
        values.push(sampleValue)
        dataPoints.push({
            timestamp_dispositivo: nowTimestamp - sampleIndex * CHART_SAMPLE_INTERVAL_IN_MINUTES * 60,
            [fieldName]: sampleValue,
        })
    }

    const minimumValue = Math.min(...values)
    const maximumValue = Math.max(...values)
    const averageValue = values.reduce((total, item) => total + item, 0) / values.length

    return {
        group: 'hour',
        field: fieldName,
        campo: {
            id: campoMeta.id,
            identificacao: campoMeta.identificacao || fieldName,
            descricao: campoMeta.descricao || fieldName,
            unidade: campoMeta.unidade || '',
            configuracao: campoMeta.configuracao || '{}',
        },
        data: {
            [DEMO_MQTT_USER]: dataPoints,
        },
        extra: {
            min: Number(minimumValue.toFixed(2)),
            med: Number(averageValue.toFixed(2)),
            max: Number(maximumValue.toFixed(2)),
        },
    }
}

export const buildConsumoSeries = (fieldName = 'volume_hidrometro') => {
    const groupByHour = {}
    let totalDiff = 0

    for (let hour = 0; hour < 24; hour += 1) {
        const hourKey = String(hour)
        const hourDiff = Number(
            (baseReadingForField(fieldName, hour) * 0.65 + (hour % 4) * 0.4).toFixed(2)
        )
        groupByHour[hourKey] = { diff: hourDiff }
        totalDiff += hourDiff
    }

    return {
        data: {
            TOTAL_GROUP: groupByHour,
            TOTAL: Number(totalDiff.toFixed(2)),
            PROJECTION: Number((totalDiff * 1.15).toFixed(2)),
        },
    }
}

export const buildStatusReportTableRows = (fieldNames = []) => {
    const nowTimestamp = Math.floor(Date.now() / 1000)
    const rows = []

    for (let sampleIndex = 0; sampleIndex < 48; sampleIndex += 1) {
        const timestampDispositivo = nowTimestamp - sampleIndex * 1800
        const hourOffset = sampleIndex / 2
        const dateObject = new Date(timestampDispositivo * 1000)
        const dataHora = dateObject
            .toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            })
            .replace(',', '')

        const row = {
            data_hora: dataHora,
            timestamp_dispositivo: timestampDispositivo,
        }

        fieldNames.forEach((fieldName) => {
            row[fieldName] = baseReadingForField(fieldName, hourOffset)
        })

        rows.push(row)
    }

    return {
        count: rows.length,
        next: null,
        previous: null,
        lastKey: null,
        results: rows,
    }
}

export const buildSupervisorioReporte = (identificacoes = []) => {
    const latestValues = getLatestSandboxValues()
    const parametricIdentifiers =
        identificacoes.length > 0
            ? identificacoes
            : DEMO_SANDBOX_PARAM_DEFINITIONS.map((definition) => definition.identificacao)

    const unidades = {}
    parametricIdentifiers.forEach((identificacao) => {
        const definition = DEMO_SANDBOX_PARAM_DEFINITIONS.find(
            (item) => item.identificacao === identificacao
        )
        unidades[identificacao] = definition?.unidade || ''
    })

    const deviceReading = {
        ...latestValues,
        id_dispositivo: DEMO_MQTT_USER,
        unidades,
        _parametros: parametricIdentifiers.map((identificacao) => {
            const definition = DEMO_SANDBOX_PARAM_DEFINITIONS.find(
                (item) => item.identificacao === identificacao
            )
            return {
                identificacao,
                unidade: definition?.unidade || '',
                descricao: definition?.descricao || identificacao,
            }
        }),
    }

    return {
        [DEMO_IMEI]: deviceReading,
    }
}

const HISTORICO_SAMPLE_INTERVAL_IN_SECONDS = 900

export const buildSupervisorioHistorico = (requestBody = {}) => {
    const totalSeconds = Number(requestBody.horas_historico_em_segundos) || 86400
    const totalPoints = Math.min(
        Math.max(Math.round(totalSeconds / HISTORICO_SAMPLE_INTERVAL_IN_SECONDS), 8),
        96
    )
    const nowTimestamp = Math.floor(Date.now() / 1000)
    const categories = []

    for (let pointIndex = totalPoints - 1; pointIndex >= 0; pointIndex -= 1) {
        categories.push(nowTimestamp - pointIndex * HISTORICO_SAMPLE_INTERVAL_IN_SECONDS)
    }

    const requestedDevices = Array.isArray(requestBody.equipamentos_ids_parametros)
        ? requestBody.equipamentos_ids_parametros
        : []

    const requestedFieldNames = requestedDevices
        .flatMap((device) => String(device?.parametros || '').split(','))
        .map((fieldName) => fieldName.trim())
        .filter(Boolean)

    const fieldNames = requestedFieldNames.length ? requestedFieldNames : ['nivel']

    const series = fieldNames.map((fieldName) => ({
        name: `${DEMO_IMEI}_${fieldName}`,
        type: 'line',
        data: categories.map((timestamp, pointIndex) =>
            baseReadingForField(
                fieldName,
                ((totalPoints - pointIndex) * HISTORICO_SAMPLE_INTERVAL_IN_SECONDS) / 3600
            )
        ),
    }))

    return { series, categories }
}
